import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Activity } from '../types';
import { retry } from '../utils/async';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

export function useRecentActivity() {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchWithRetry = async <T,>(operation: () => Promise<{ data: T | null; error: any }>) => {
    try {
      return await retry(async () => {
        const { data, error } = await operation();
        if (error) throw error;
        return data;
      }, MAX_RETRIES, RETRY_DELAY);
    } catch (err) {
      throw err;
    }
  };

  const fetchRecentActivity = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get authenticated user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('Not authenticated');

      // Fetch all data in parallel
      const [notes, tasksResponse, transactions, challenges] = await Promise.all([
        // Fetch recent brain dump notes
        fetchWithRetry(() => 
          supabase
            .from('notes')
            .select('id, content, created_at')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(5)
        ),

        // Fetch recent tasks
        supabase
          .from('tasks')
          .select(`
            id,
            title,
            description,
            role,
            created_at,
            priority,
            status,
            task_collaborators (
              user_id,
              role
            )
          `)
          .eq('user_id', user.id)
          .neq('status', 'done')
          .eq('archived', false)
          .order('created_at', { ascending: false })
          .limit(5),

        // Fetch recent transactions
        fetchWithRetry(() => 
          supabase
            .from('business_transactions')
            .select('id, type, business_name, amount, date, created_at')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(5)
        ),

        // Fetch recent challenges
        fetchWithRetry(() => 
          supabase
            .from('challenges')
            .select('*')
            .eq('is_active', true)
            .order('created_at', { ascending: false })
            .limit(5)
        )
      ]);

      if (tasksResponse.error) throw tasksResponse.error;
      const tasks = tasksResponse.data || [];

      // Convert to activities with proper type checking
      const allActivities = [
        ...(notes || []).map(note => ({
          id: note.id,
          type: 'brain-dump' as const,
          title: note.content?.title || 'Untitled Note',
          timestamp: new Date(note.created_at),
        })),
        ...tasks.map(task => ({
          id: task.id,
          type: 'task' as const,
          title: task.title || 'Untitled Task',
          description: task.description,
          role: task.role,
          timestamp: new Date(task.created_at || new Date()),
          priority: task.priority,
          status: task.status,
        })),
        ...(transactions || []).map(tx => ({
          id: tx.id,
          type: tx.type as 'invoice' | 'expense',
          title: tx.business_name,
          timestamp: new Date(tx.created_at),
          amount: tx.amount,
        })),
        ...(challenges || []).map(challenge => ({
          id: challenge.id,
          type: 'challenge' as const,
          title: challenge.title,
          description: challenge.description,
          reward: challenge.reward,
          timestamp: new Date(challenge.created_at)
        }))
      ].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

      setActivities(allActivities);
      setError(null);
    } catch (err) {
      console.error('Error fetching recent activity:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch activities'));
      // Keep existing activities on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentActivity();
  }, []);

  return {
    activities,
    loading,
    error,
    refresh: fetchRecentActivity,
  };
}